import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { submitLead } from '@/lib/api'
import { track } from '@/lib/analytics'
import { useLocale } from '@/i18n/locale'
import { TG_PERSONAL, TG_PERSONAL_URL, TG_CHANNEL_URL } from '@/lib/contacts'

/** Схема строится под локаль — тексты ошибок валидации локализованы.
 *  Ключи enum (revenue) НЕ меняются: бэкенд и Метрика ждут те же.
 *
 *  Поля «Ожидаемый бюджет проекта» здесь больше нет (аудит 2026-09-21):
 *  рядом написано «на созвоне не продаём», а вопрос о бюджете до разговора
 *  скептик читал как маркер продажи. Вопрос переезжает в бриф шага 02.
 *  В API (functions/api/submit.ts, src/lib/api.ts) budget остаётся
 *  необязательным — заявку без него принимают как раньше. */
function makeSchema(t: (typeof COPY)['ru']) {
  return z.object({
    name: z.string().min(2, t.errName),
    // Мягкая валидация: строка годится, если содержит @ (ник или email)
    // либо не меньше 7 цифр (телефон). Строгий шаблон отсекал бы живые
    // варианты записи, а «тг» и опечатки пропускал бы min(3).
    contact: z.string().refine((v) => /@/.test(v) || (v.replace(/\D/g, '').length >= 7), t.errContact),
    revenue: z.enum(['50-100', '100-300', '300-1000', '1000+'], {
      required_error: t.errRevenue,
    }),
    task: z.string().optional(),
    consent: z.literal(true, { errorMap: () => ({ message: t.errConsent }) }),
    website: z.string().max(0, 'bot').optional().or(z.literal('')),
  })
}

const COPY = {
  ru: {
    cardTitle: 'Запись на бесплатную диагностику',
    cardSub: '30–90 минут · проводит лично Антон · на созвоне не продаём',
    labelName: 'Имя', phName: 'Как к вам обращаться',
    labelContact: 'Telegram, телефон или email', phContact: '@username, +7… или email',
    labelRevenue: 'Выручка компании', phRevenue: 'Выберите диапазон',
    hintRevenue: 'Нужна, чтобы подготовить разбор под ваш масштаб',
    optional: 'необязательно',
    labelTask: 'Задача', phTask: 'Можно пропустить — обсудим на созвоне',
    consentPre: 'Даю согласие на обработку персональных данных в соответствии с ',
    consentLink: 'Политикой конфиденциальности',
    submit: 'Записаться на диагностику', submitting: 'Отправляем…',
    errName: 'Укажите имя', errContact: 'Нужен ник, телефон или email — чтобы было куда ответить',
    errRevenue: 'Выберите диапазон выручки', errConsent: 'Нужно согласие на обработку ПД',
    okTitle: 'Заявка принята',
    okBody: 'В рабочее время отвечаем в течение часа, иначе — следующим рабочим утром. Следующий шаг: короткий бриф на 3–5 вопросов, чтобы диагностика началась с сути.',
    okSub: 'А пока — подпишитесь на канал Антона. Практика внедрения ИИ в реальные бизнесы, без инфоцыганства.',
    okBtn: 'Подписаться на @ro_baten', okBtnAria: 'Подписаться на Telegram-канал @ro_baten',
    errPre: 'Что-то пошло не так. Напишите напрямую: ',
    revenue: [
      ['50-100', '50–100 млн ₽'], ['100-300', '100–300 млн ₽'],
      ['300-1000', '300 млн – 1 млрд ₽'], ['1000+', '1 млрд+ ₽'],
    ],
    privacyPath: '/privacy/',
  },
  en: {
    cardTitle: 'Book your free diagnostics',
    cardSub: '30–90 minutes · run by Anton personally · no selling on the call',
    labelName: 'Name', phName: 'How should we address you',
    labelContact: 'Email, Telegram or phone', phContact: 'Email, @username or +1…',
    labelRevenue: 'Company revenue', phRevenue: 'Select a range',
    hintRevenue: 'We use it to prepare the review for your scale',
    optional: 'optional',
    labelTask: 'Your challenge', phTask: 'You can skip this — we’ll discuss on the call',
    consentPre: 'I consent to the processing of my personal data per the ',
    consentLink: 'Privacy Policy',
    submit: 'Book AI diagnostics', submitting: 'Sending…',
    errName: 'Enter your name', errContact: 'We need an email, handle or phone to reply to',
    errRevenue: 'Select a revenue range', errConsent: 'Consent to data processing is required',
    okTitle: 'Request received',
    okBody: 'During working hours we reply within an hour, otherwise the next working morning. Next step: a short 3–5 question brief so the diagnostics starts on substance.',
    okSub: 'Meanwhile — follow Anton’s channel. Real-world AI implementation, no hype.',
    okBtn: 'Follow @ro_baten', okBtnAria: 'Follow Telegram channel @ro_baten',
    errPre: 'Something went wrong. Reach out directly: ',
    revenue: [
      ['50-100', '$0.5–1M'], ['100-300', '$1–3M'],
      ['300-1000', '$3–10M'], ['1000+', '$10M+'],
    ],
    privacyPath: '/privacy/',
  },
}

type FormData = z.infer<ReturnType<typeof makeSchema>>

export function LeadForm() {
  const t = useLocale() === 'en' ? COPY.en : COPY.ru
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle')
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(makeSchema(t)) })

  const onSubmit = async (data: FormData) => {
    if (data.website) return // honeypot trip
    setStatus('sending')
    try {
      await submitLead({
        name: data.name,
        contact: data.contact,
        revenue: data.revenue,
        task: data.task,
      })
      setStatus('ok')
      track('form_submit', { revenue: data.revenue })
      track('form_success', { revenue: data.revenue })
    } catch (e) {
      setStatus('error')
      track('form_error', { error: String(e) })
    }
  }

  if (status === 'ok') {
    return (
      <div className="card">
        <div className="text-h3 text-ink">{t.okTitle}</div>
        <p className="mt-3 text-body text-muted">{t.okBody}</p>
        <div className="mt-6 pt-6 border-t border-divider">
          <p className="text-small text-muted">{t.okSub}</p>
          <a
            href={TG_CHANNEL_URL}
            target="_blank"
            rel="noreferrer"
            className="tg-link mt-4 inline-flex items-center gap-2 btn-secondary no-underline"
            aria-label={t.okBtnAria}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.568 8.16-1.86 8.773c-.14.622-.507.775-1.027.482l-2.84-2.092-1.37 1.318c-.152.152-.28.28-.574.28l.204-2.907 5.29-4.78c.23-.205-.05-.32-.357-.115l-6.54 4.117-2.82-.88c-.612-.193-.624-.612.128-.907l11.027-4.25c.51-.185.957.122.79.89z" />
            </svg>
            {t.okBtn}
          </a>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onFocus={() => track('form_start')} className="card" noValidate>
      {/* Заголовок внутри карточки: липкая кнопка ведёт прямо на #lead-form, и
          без него человек приземлялся на поле «Имя», не видя, на что записывается. */}
      <h3 className="text-h3 text-ink">{t.cardTitle}</h3>
      <p className="mt-2 mb-8 text-small text-muted">{t.cardSub}</p>
      <div className="space-y-6">
        <div>
          <label htmlFor="lead-name" className="text-small text-muted block mb-1">{t.labelName}</label>
          <input id="lead-name" className="input-underline" {...register('name')} placeholder={t.phName} autoComplete="name" />
          {errors.name && <p className="text-small text-danger mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="lead-contact" className="text-small text-muted block mb-1">{t.labelContact}</label>
          <input id="lead-contact" className="input-underline" {...register('contact')} placeholder={t.phContact} autoComplete="off" inputMode="text" />
          {errors.contact && <p className="text-small text-danger mt-1">{errors.contact.message}</p>}
        </div>

        <div>
          <label htmlFor="lead-revenue" className="text-small text-muted block mb-1">{t.labelRevenue}</label>
          <select
            id="lead-revenue"
            className="input-underline"
            {...register('revenue')}
            defaultValue=""
            aria-describedby="lead-revenue-hint"
          >
            <option value="" disabled>{t.phRevenue}</option>
            {t.revenue.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {/* Единственное обязательное поле, которое просит цифру, — говорим зачем. */}
          <p id="lead-revenue-hint" className="text-small text-muted mt-1">{t.hintRevenue}</p>
          {errors.revenue && <p className="text-small text-danger mt-1">{errors.revenue.message}</p>}
        </div>

        <div>
          <label htmlFor="lead-task" className="text-small text-muted block mb-1">
            {t.labelTask} · <span>{t.optional}</span>
          </label>
          <textarea id="lead-task" className="input-underline min-h-[3rem] resize-none" rows={2} {...register('task')} placeholder={t.phTask} />
          {errors.task && <p className="text-small text-danger mt-1">{errors.task.message}</p>}
        </div>

        {/* Honeypot */}
        <div aria-hidden className="absolute -left-[9999px] opacity-0 pointer-events-none">
          <label>Website <input tabIndex={-1} autoComplete="off" {...register('website')} /></label>
        </div>

        <div className="flex items-start gap-3">
          <input id="consent" type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[rgb(var(--accent-rgb))]" {...register('consent')} />
          <label htmlFor="consent" className="text-small text-muted">
            {t.consentPre}
            <a href="/privacy" onClick={(e) => { e.preventDefault(); window.location.assign(t.privacyPath) }}>
              {t.consentLink}
            </a>.
          </label>
        </div>
        {errors.consent && <p className="text-small text-danger">{errors.consent.message}</p>}

        <button type="submit" disabled={status === 'sending'} className="btn-primary w-full whitespace-nowrap px-4 disabled:opacity-60">
          {status === 'sending' ? t.submitting : t.submit}
        </button>

        {status === 'error' && (
          <p className="text-small text-danger">
            {t.errPre}
            <a href={TG_PERSONAL_URL} target="_blank" rel="noreferrer" className="tg-link">t.me/{TG_PERSONAL}</a>.
          </p>
        )}
      </div>
    </form>
  )
}
